import { FUNNY_LOADING_MESSAGES } from "@repo/sync-utils/src/funny-loading-messages";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

import { pickRandomLoadingMessageIndex } from "./comic-loading-message";
import { SyncLoadingMark } from "./SyncLoadingMark";

const COMIC_LOADING_MESSAGE_VISIBLE_MS = 2_400;

export function ComicLoadingScreen({ fixed = false }: { fixed?: boolean }) {
  const [messageIndex, setMessageIndex] = useState(() =>
    pickRandomLoadingMessageIndex(-1, FUNNY_LOADING_MESSAGES.length),
  );
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setMessageIndex((current) =>
        pickRandomLoadingMessageIndex(current, FUNNY_LOADING_MESSAGES.length),
      );
    }, COMIC_LOADING_MESSAGE_VISIBLE_MS);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      minHeight={fixed ? undefined : "100dvh"}
      px={{ xs: 3, sm: 5 }}
      spacing={{ xs: 3, md: 4 }}
      textAlign="center"
      role="status"
      aria-live="polite"
      sx={
        fixed
          ? {
              bgcolor: "background.default",
              inset: 0,
              position: "fixed",
              zIndex: 99999,
            }
          : undefined
      }
    >
      <SyncLoadingMark size={100} />
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          maxWidth: 760,
          minHeight: { xs: 64, md: 76 },
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        <AnimatePresence initial={false} mode="wait">
          <Box
            component={motion.div}
            key={messageIndex}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{
              duration: reduceMotion ? 0 : 0.38,
              ease: "easeOut",
            }}
            sx={{ position: "absolute", width: "100%" }}
          >
            <Typography
              fontSize={{ xs: "1.25rem", sm: "1.25rem", md: "1.3rem" }}
              fontWeight={400}
              lineHeight={1.3}
              sx={{
                color: (theme) => alpha(theme.palette.primary.dark, 0.6),
              }}
            >
              {FUNNY_LOADING_MESSAGES[messageIndex]}
            </Typography>
          </Box>
        </AnimatePresence>
      </Box>
    </Stack>
  );
}
